import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "@/lib/motion-lite";
import {
  Gauge, Gamepad2, Tv, Video, Server, ShieldCheck, Wifi,
  Sparkles, ArrowRight, CheckCircle2, Activity, Share2, HelpCircle, Layers,
  FileSearch, GitCompareArrows, FolderOpen,
} from "lucide-react";
import { useI18n, injectJsonLd } from "@/i18n";
import { api } from "@/lib/api";
import { HeroGauge } from "@/components/landing/HeroGauge";
import { HeroStats } from "@/components/landing/HeroStats";
import { DiscoverSection } from "@/components/landing/DiscoverSection";
import { HomeMapPreview } from "@/components/landing/HomeMapPreview";
import { AccountSection } from "@/components/landing/AccountSection";

const LANG_SLUG = { pl: "poland", en: "united-kingdom", de: "germany", es: "spain", fr: "france", it: "italy", pt: "portugal", tr: "turkey", ru: "russia", ar: "saudi-arabia", hi: "india", zh: "china", ja: "japan", ko: "south-korea" };

const FEAT_ICONS = [Gauge, Sparkles, Gamepad2, Tv, Video, Server, ShieldCheck, Wifi];
const DC_ICONS = [Share2];
const DC_LINKS = ["/test"];

export default function Landing() {
  const { t, lp, lang } = useI18n();
  const features = t("landing.f");
  const dataCards = t("landing.dc");
  const faq = t("landing.faq");
  const [testCount, setTestCount] = useState(0);
  const [overview, setOverview] = useState(null);
  const countrySlug = LANG_SLUG[lang] || "united-kingdom";

  useEffect(() => {
    api.get("/stats/count").then((r) => setTestCount(r.data.count || 0)).catch(() => {});
    api.get("/stats/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    injectJsonLd("faq-jsonld", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (Array.isArray(faq) ? faq : []).map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    });
    return () => injectJsonLd("faq-jsonld", null);
  }, [lang]); // eslint-disable-line

  return (
    <div>
      <section className="grid-bg hero-glow relative overflow-hidden border-b border-border">
        <HeroGauge />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-12 pt-6 md:grid-cols-2 md:px-8 md:py-24">
          <div>
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-sm font-500">
              <span className="h-2 w-2 rounded-full bg-success" /> {t("landing.badge")}
            </motion.span>
            {testCount > 0 && (
              <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}
                data-testid="test-counter"
                className="ml-2 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-sm font-600 text-primary">
                <Activity className="h-3.5 w-3.5" /> {testCount.toLocaleString()} {t("landing.testsWord")}
              </motion.span>
            )}
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="mt-5 font-head text-4xl font-800 leading-[1.05] sm:text-5xl lg:text-6xl">
              {t("landing.h1a")}<span className="text-primary">{t("landing.h1b")}</span>{t("landing.h1c")}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
              {t("landing.lead")}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="mt-8 flex flex-wrap gap-3">
              <Link to={lp("/test")} data-testid="hero-start-btn" className="hero-cta flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-lg font-800 text-primary-foreground transition-transform hover:-translate-y-0.5 active:scale-[0.98]">
                <Gauge className="h-6 w-6" /> {t("landing.ctaStart")}
              </Link>
              <Link to={lp(`/rankings/${countrySlug}`)} data-testid="hero-ranking-btn" className="flex items-center gap-2 rounded-full border border-border bg-card px-6 py-4 text-base font-600 transition-colors hover:bg-accent">
                {t("disc.ctaRanking")} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
            <div className="mt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
              {[t("landing.b1"), t("landing.b2"), t("landing.b3")].map((tx) => (
                <span key={tx} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-success" />{tx}</span>
              ))}
            </div>
            <HeroStats data={overview} t={t} />
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}
            className="rounded-3xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-head font-700"><Activity className="h-5 w-5 text-primary" /> {t("landing.resultTitle")}</span>
              <span className="rounded-full bg-success/10 px-3 py-1 text-xs font-600 text-emerald-700 dark:text-success">{t("landing.excellent")}</span>
            </div>
            <div className="mt-6 flex items-end gap-2">
              <span className="font-head text-7xl font-800 text-primary">92</span>
              <span className="mb-2 text-2xl text-muted-foreground">/100</span>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[["Download", "512", "Mbps"], ["Upload", "410", "Mbps"], ["Ping", "8", "ms"]].map(([l, v, u]) => (
                <div key={l} className="rounded-xl border border-border bg-secondary/50 p-3">
                  <div className="text-xs text-muted-foreground">{l}</div>
                  <div className="font-mono text-xl font-700">{v}<span className="text-xs text-muted-foreground"> {u}</span></div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {[t("score.streaming4k"), t("score.gaming"), t("score.videocalls")].map((tx) => (
                <div key={tx} className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-success" /> {tx}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <DiscoverSection t={t} lp={lp} countrySlug={countrySlug} />

      <HomeMapPreview t={t} lp={lp} countrySlug={countrySlug} />

      <AccountSection t={t} lp={lp} users={overview?.users || 0} />

      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8">
        <h2 className="font-head text-3xl font-700 md:text-4xl">{t("landing.featTitle")}</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">{t("landing.featLead")}</p>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => {
            const Icon = FEAT_ICONS[i];
            return (
              <motion.div key={f.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }}
                className="card-hover rounded-2xl border border-border bg-card p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
                <h3 className="mt-4 font-head text-lg font-700">{f.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="border-y border-border bg-secondary">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-14 md:grid-cols-3 md:px-8">
          {dataCards.slice(2).map((c, i) => {
            const Icon = DC_ICONS[i];
            return (
              <Link key={c.t} to={lp(DC_LINKS[i])} className="card-hover group rounded-2xl border border-border bg-card p-6">
                <Icon className="h-7 w-7 text-primary" />
                <h3 className="mt-4 font-head text-xl font-700">{c.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-600 text-primary">{c.a} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40" data-testid="landing-site-tools">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3.5 py-1 text-xs font-700 uppercase tracking-wide text-primary">
            <Sparkles className="h-3.5 w-3.5" /> {t("stools.badge")}
          </span>
          <h2 className="mt-4 font-head text-3xl font-700 md:text-4xl">{t("stools.title")}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">{t("stools.lead")}</p>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              [FileSearch, "c1", "/sites/audit"],
              [GitCompareArrows, "c2", "/sites/compare"],
              [FolderOpen, "c3", "/sites/catalog"],
            ].map(([Icon, k, to], i) => (
              <motion.div key={k} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                <Link to={lp(to)} data-testid={`stool-${k}`} className="card-hover group flex h-full flex-col rounded-2xl border border-border bg-card p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
                  <h3 className="mt-4 font-head text-xl font-700">{t(`stools.${k}t`)}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">{t(`stools.${k}d`)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-600 text-primary">{t(`stools.${k}a`)} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:px-8" data-testid="how-we-test">
        <h2 className="font-head text-3xl font-700 md:text-4xl">{t("xtra.howTitle")}</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">{t("xtra.howLead")}</p>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Layers, t("xtra.how1t"), t("xtra.how1d")],
            [Activity, t("xtra.how2t"), t("xtra.how2d")],
            [CheckCircle2, t("xtra.how3t"), t("xtra.how3d")],
            [Sparkles, t("xtra.how4t"), t("xtra.how4d")],
          ].map(([Icon, title, desc], i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="relative overflow-hidden rounded-2xl border border-border bg-card p-6">
              <span className="absolute -right-3 -top-3 font-head text-6xl font-800 text-primary/5">{i + 1}</span>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
              <h3 className="mt-4 font-head text-lg font-700">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-4 py-16 md:px-8">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-6 w-6 text-primary" />
            <h2 className="font-head text-3xl font-700 md:text-4xl">{t("landing.faqTitle")}</h2>
          </div>
          <div className="mt-8 space-y-3">
            {(Array.isArray(faq) ? faq : []).map((item, i) => (
              <details key={i} className="group rounded-2xl border border-border bg-card p-5" data-testid={`faq-${i}`}>
                <summary className="flex cursor-pointer list-none items-center justify-between font-head text-base font-600">
                  {item.q}
                  <ArrowRight className="h-4 w-4 text-primary transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 text-center md:px-8">
        <h2 className="mx-auto max-w-2xl font-head text-3xl font-800 md:text-4xl">{t("landing.ctaBottomTitle")}</h2>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">{t("landing.ctaBottomLead")}</p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to={lp("/test")} className="rounded-full bg-primary px-8 py-3.5 text-base font-700 text-primary-foreground transition-transform hover:-translate-y-0.5">{t("landing.ctaBottomStart")}</Link>
          <Link to={lp("/register")} className="rounded-full border border-border px-8 py-3.5 text-base font-600 transition-colors hover:bg-accent">{t("landing.ctaBottomRegister")}</Link>
        </div>
      </section>
    </div>
  );
}
